import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import { seoTopicRegistry } from "../seoTopicRegistry";
import { peekSeoLearningReturnPath, rememberSeoLearningReturnPath } from "../demo/seoLearningResume";
import { trackMarketingEvent } from "../utils/marketingMeasurement";
import {
  SEO_GUEST_LIMIT_MS,
  SEO_GUEST_PROMPT_MS,
  SEO_GUEST_STARTED_AT_KEY,
} from "../demo/guestLearningLimits";

const DISMISSED_KEY = "matematron.seoLearning.promptDismissed.v2";

function formatCampaignDate(value, fallback) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

function readStartedAt() {
  try {
    const stored = Number(window.localStorage.getItem(SEO_GUEST_STARTED_AT_KEY));
    if (Number.isFinite(stored) && stored > 0) return stored;
    const now = Date.now();
    window.localStorage.setItem(SEO_GUEST_STARTED_AT_KEY, String(now));
    return now;
  } catch {
    return Date.now();
  }
}

export default function SeoGuestLearningGate() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [startedAt, setStartedAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [campaign, setCampaign] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem(DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const matchedTopic = useMemo(
    () => seoTopicRegistry.find((topic) => `/${topic.slug}` === location.pathname) || null,
    [location.pathname]
  );
  const isGenericSeoTopic = !!matchedTopic && !String(matchedTopic.seoKind || "").startsWith("matura");
  const isTopicDemo = location.pathname.startsWith("/demo-temat/");
  const active = !user && (isGenericSeoTopic || isTopicDemo);

  useEffect(() => {
    if (!active) return undefined;
    setStartedAt(readStartedAt());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    const syncAcrossTabs = (event) => {
      if (event.key !== SEO_GUEST_STARTED_AT_KEY) return;
      const sharedStartedAt = Number(event.newValue);
      if (Number.isFinite(sharedStartedAt) && sharedStartedAt > 0) {
        setStartedAt(sharedStartedAt);
        setNow(Date.now());
      }
    };
    window.addEventListener("storage", syncAcrossTabs);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("storage", syncAcrossTabs);
    };
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    fetch("/api/payments/launch-campaign/public", { credentials: "include" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => !cancelled && setCampaign(data?.open === true ? data : null))
      .catch(() => !cancelled && setCampaign(null));
    return () => {
      cancelled = true;
    };
  }, [active]);

  if (!active || startedAt == null) return null;

  const elapsed = Math.max(0, now - startedAt);
  const expired = elapsed >= SEO_GUEST_LIMIT_MS;
  const showPrompt = elapsed >= SEO_GUEST_PROMPT_MS && !dismissed && !expired;
  const campaignMessage = campaign
    ? `Załóż konto do ${formatCampaignDate(campaign.offerEndsAt, "końca promocji")} i aktywuj bezpłatny dostęp do ${formatCampaignDate(campaign.accessValidTo, "końca roku")}.`
    : "Załóż konto, aby zachować wyniki i kontynuować naukę bez limitu gościa.";
  const remember = () => {
    // Strona startowa SEO zapisuje wcześniej dokładny cel teorii. Nie zastępujemy
    // go ogólnym adresem landing page; w ćwiczeniach i quizie zapisujemy bieżący widok.
    if (!matchedTopic || !peekSeoLearningReturnPath()) rememberSeoLearningReturnPath();
    trackMarketingEvent("seo_learning_registration_click", {
      placement: expired ? "limit_dialog" : "delayed_prompt",
      seo_slug: matchedTopic?.slug || "topic_demo",
    });
  };

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISSED_KEY, "1");
    } catch {}
    setDismissed(true);
  };

  if (expired) {
    return (
      <div role="dialog" aria-modal="true" aria-labelledby="seo-access-ended-title" style={{ position: "fixed", inset: 0, zIndex: 15000, display: "grid", placeItems: "center", padding: 18, background: "rgba(0,0,0,.76)", backdropFilter: "blur(8px)" }}>
        <section className="glass-container" style={{ width: "min(540px, 100%)", boxSizing: "border-box", padding: 24, borderRadius: 18, border: "1px solid rgba(255,167,38,.8)", textAlign: "center" }}>
          <h2 id="seo-access-ended-title" style={{ margin: "0 0 10px" }}>Kontynuuj dokładnie od tego miejsca</h2>
          <p style={{ margin: "0 auto 18px", maxWidth: 450, lineHeight: 1.55, opacity: .84 }}>
            Podgląd dla gościa trwa 10 minut. Zaloguj się lub załóż konto, aby wrócić dokładnie do tego miejsca, zapisywać wyniki quizów i oglądać podejścia w sekcji „Mój postęp”. {campaignMessage}
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
            <Link className="btn-secondary" to="/login" onClick={remember} style={{ textDecoration: "none" }}>Zaloguj się</Link>
            <Link className="btn-primary" to="/register" onClick={remember} style={{ textDecoration: "none" }}>Załóż bezpłatne konto</Link>
          </div>
        </section>
      </div>
    );
  }

  return showPrompt ? (
    <div role="dialog" aria-modal="true" aria-labelledby="seo-progress-prompt-title" style={{ position: "fixed", inset: 0, zIndex: 14000, display: "grid", placeItems: "center", padding: 18, background: "rgba(0,0,0,.78)", backdropFilter: "blur(8px)" }}>
      <section className="glass-container" style={{ position: "relative", width: "min(580px, 100%)", boxSizing: "border-box", padding: "28px 24px 24px", borderRadius: 18, border: "2px solid rgba(255,167,38,.88)", background: "var(--color-bg)", color: "var(--color-text)", boxShadow: "0 24px 80px rgba(0,0,0,.62)", textAlign: "center" }}>
        <button type="button" aria-label="Zamknij przypomnienie" title="Zamknij" onClick={dismiss} className="btn-secondary" style={{ position: "absolute", top: 12, right: 12, width: 42, height: 42, minWidth: 42, padding: 0, borderRadius: 11, fontSize: 22, lineHeight: 1 }}>×</button>
        <h2 id="seo-progress-prompt-title" style={{ margin: "8px 46px 10px" }}>Zapisz postęp w tym zagadnieniu</h2>
        <p style={{ margin: "0 auto 8px", maxWidth: 470, lineHeight: 1.55, opacity: .9 }}>
          Konto pozwoli Ci wrócić dokładnie do tej teorii, ćwiczeń albo quizu i zachować wyniki.
        </p>
        <p style={{ margin: "0 auto 20px", maxWidth: 470, lineHeight: 1.55, fontWeight: 750 }}>
          {campaignMessage}
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
          <Link className="btn-secondary" to="/login" onClick={remember} style={{ textDecoration: "none" }}>Zaloguj się</Link>
          <Link className="btn-primary" to="/register" onClick={remember} style={{ textDecoration: "none" }}>Załóż konto</Link>
        </div>
      </section>
    </div>
  ) : null;
}

export const seoGuestLearningGateConfig = {
  softPromptMs: SEO_GUEST_PROMPT_MS,
  accessLimitMs: SEO_GUEST_LIMIT_MS,
  startedAtKey: SEO_GUEST_STARTED_AT_KEY,
};
